import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Droplet, Trash2, CalendarDays } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, differenceInDays } from "date-fns";

type FlowIntensity = "spotting" | "light" | "medium" | "heavy";

interface PeriodLog {
  id: string;
  log_date: string;
  flow_intensity: FlowIntensity;
  notes: string | null;
}

const flowOptions: { value: FlowIntensity; label: string; color: string }[] = [
  { value: "spotting", label: "Spotting", color: "bg-pink-200 text-pink-800" },
  { value: "light", label: "Light", color: "bg-pink-300 text-pink-900" },
  { value: "medium", label: "Medium", color: "bg-pink-400 text-white" },
  { value: "heavy", label: "Heavy", color: "bg-pink-600 text-white" },
];

export const PeriodTracker = () => {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [periodLogs, setPeriodLogs] = useState<PeriodLog[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<FlowIntensity | null>(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch all period logs
  useEffect(() => {
    fetchPeriodLogs();
  }, []);

  const fetchPeriodLogs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("period_logs")
        .select("*")
        .eq("user_id", user.id)
        .order("log_date", { ascending: false });

      if (error) throw error;
      setPeriodLogs((data || []).map(log => ({
        ...log,
        flow_intensity: log.flow_intensity as FlowIntensity
      })));
    } catch (error) {
      console.error("Error fetching period logs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load log for selected date
  useEffect(() => {
    if (selectedDate) {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const existingLog = periodLogs.find((log) => log.log_date === dateStr);
      if (existingLog) {
        setSelectedFlow(existingLog.flow_intensity);
        setNotes(existingLog.notes || "");
      } else {
        setSelectedFlow(null);
        setNotes("");
      }
    }
  }, [selectedDate, periodLogs]);

  const handleLogPeriod = async () => {
    if (!selectedDate || !selectedFlow) return;

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const existingLog = periodLogs.find((log) => log.log_date === dateStr);

      if (existingLog) {
        // Update existing
        const { error } = await supabase
          .from("period_logs")
          .update({
            flow_intensity: selectedFlow,
            notes: notes || null,
          })
          .eq("id", existingLog.id);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from("period_logs")
          .insert({
            user_id: user.id,
            log_date: dateStr,
            flow_intensity: selectedFlow,
            notes: notes || null,
          });

        if (error) throw error;
      }

      toast({
        title: "Period logged",
        description: `Recorded ${selectedFlow} flow for ${format(selectedDate, "MMM d, yyyy")}`,
      });

      fetchPeriodLogs();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLog = async (id: string) => {
    try {
      const { error } = await supabase
        .from("period_logs")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Deleted",
        description: "Period log removed",
      });

      fetchPeriodLogs();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Get dates grouped by flow intensity for calendar highlighting
  const spottingDates = periodLogs.filter(log => log.flow_intensity === "spotting").map(log => new Date(log.log_date));
  const lightDates = periodLogs.filter(log => log.flow_intensity === "light").map(log => new Date(log.log_date));
  const mediumDates = periodLogs.filter(log => log.flow_intensity === "medium").map(log => new Date(log.log_date));
  const heavyDates = periodLogs.filter(log => log.flow_intensity === "heavy").map(log => new Date(log.log_date));

  // Calculate last period and cycle info
  const getLastPeriodInfo = () => {
    if (periodLogs.length === 0) return null;

    const sortedLogs = [...periodLogs].sort(
      (a, b) => new Date(b.log_date).getTime() - new Date(a.log_date).getTime()
    );

    const lastDate = new Date(sortedLogs[0].log_date);
    const daysSince = differenceInDays(new Date(), lastDate);

    return { lastDate, daysSince };
  };

  const lastPeriodInfo = getLastPeriodInfo();

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="p-6 bg-gradient-to-br from-pink-50 via-background to-pink-50/50 dark:from-pink-950/20 dark:via-card dark:to-pink-950/10 border-pink-200/50 dark:border-pink-800/30">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 rounded-full bg-pink-100 dark:bg-pink-900/30">
            <CalendarDays className="h-6 w-6 text-pink-600 dark:text-pink-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Period Tracker</h3>
            <p className="text-sm text-muted-foreground">
              Track your menstrual cycle and flow intensity
            </p>
          </div>
        </div>

        {lastPeriodInfo && (
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="p-4 rounded-xl bg-background/80 dark:bg-background/40">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Last Period</p>
              <p className="text-lg font-semibold mt-1">
                {format(lastPeriodInfo.lastDate, "MMM d, yyyy")}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-background/80 dark:bg-background/40">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Days Since</p>
              <p className="text-lg font-semibold mt-1">
                {lastPeriodInfo.daysSince} days
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Calendar & Logging */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Calendar */}
        <Card className="p-4">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            disabled={(date) => date > new Date()}
            modifiers={{
              spotting: spottingDates,
              light: lightDates,
              medium: mediumDates,
              heavy: heavyDates,
            }}
            modifiersStyles={{
              spotting: {
                backgroundColor: "#fbcfe8",
                color: "#9d174d",
                borderRadius: "50%",
              },
              light: {
                backgroundColor: "#f9a8d4",
                color: "#831843",
                borderRadius: "50%",
              },
              medium: {
                backgroundColor: "#f472b6",
                color: "white",
                borderRadius: "50%",
              },
              heavy: {
                backgroundColor: "#db2777",
                color: "white",
                borderRadius: "50%",
              },
            }}
            className="w-full pointer-events-auto"
          />
        </Card>

        {/* Log Entry Form */}
        <Card className="p-6 space-y-4">
          <div>
            <h4 className="font-medium mb-2">
              {selectedDate ? format(selectedDate, "MMMM d, yyyy") : "Select a date"}
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              Select flow intensity for this day
            </p>
          </div>

          {/* Flow Intensity Selection */}
          <div className="grid grid-cols-2 gap-2">
            {flowOptions.map((option) => (
              <Button
                key={option.value}
                variant={selectedFlow === option.value ? "default" : "outline"}
                className={`justify-start gap-2 ${
                  selectedFlow === option.value
                    ? option.color
                    : "hover:bg-pink-50 dark:hover:bg-pink-950/20"
                }`}
                onClick={() => setSelectedFlow(option.value)}
              >
                <Droplet className="h-4 w-4" />
                {option.label}
              </Button>
            ))}
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm text-muted-foreground">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any symptoms, cramps, mood changes..."
              className="mt-1"
              rows={3}
            />
          </div>

          {/* Save Button */}
          <Button
            onClick={handleLogPeriod}
            disabled={!selectedFlow || saving}
            className="w-full bg-pink-600 hover:bg-pink-700"
          >
            {saving ? "Saving..." : "Log Period"}
          </Button>
        </Card>
      </div>

      {/* Recent Logs */}
      <Card className="p-6">
        <h4 className="font-medium mb-4">Recent Period Logs</h4>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : periodLogs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No period logs yet. Start tracking above!
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {periodLogs.slice(0, 10).map((log) => (
              <div
                key={log.id}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">
                    {format(new Date(log.log_date), "MMM d, yyyy")}
                  </span>
                  <Badge
                    className={
                      flowOptions.find((f) => f.value === log.flow_intensity)?.color
                    }
                  >
                    {log.flow_intensity}
                  </Badge>
                  {log.notes && (
                    <span className="text-xs text-muted-foreground truncate max-w-32">
                      {log.notes}
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteLog(log.id)}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
